import React, { useCallback, useState, useEffect } from 'react'
import { Bell, Download, Loader2 } from 'lucide-react'
import { formatDate } from 'date-fns'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  useGetNotificationsQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetActiveExportsQuery,
} from '@app.raytd.com/store'
import type { AppNotification, ReportExport } from '@app.raytd.com/store'
import { cn } from '@/lib/utils'

/**
 * NotificationBell component displays a bell icon with unread count badge
 * and opens a drawer showing all notifications.
 * 
 * Notifications are driven by the `/api/v1/notifications` endpoint (database is source of truth).
 * Real-time updates come via Laravel Echo's .notification() helper which listens for
 * BroadcastNotificationCreated events from Laravel's notification system.
 * When a notification is broadcast, it invalidates the cache and shows a transient toast.
 */
export const NotificationBell = () => {
  const { data, isLoading, isError, error, refetch, isFetching } = useGetNotificationsQuery(undefined, {
    // Fallback poll: realtime updates come via Echo, but if that socket
    // misses an event (reconnecting, not yet subscribed), a completed
    // report would otherwise only appear after a manual page refresh.
    pollingInterval: 15000,
  })
  // Use state to track whether we should poll
  // This allows RTK Query to re-evaluate the polling interval when state changes
  const [shouldPoll, setShouldPoll] = useState(false)
  
  const { data: activeExportsData, isSuccess } = useGetActiveExportsQuery(undefined, {
    // Smart polling: only poll when there are active jobs
    // State-based polling interval that updates when activeExportsData changes
    pollingInterval: shouldPoll ? 5000 : 0,
  })

  // The endpoint already returns only pending/processing jobs; this status filter is a cheap
  // guard so a regression there can never render a completed job as still generating
  // (QA test case 1.04).
  const activeExports = (activeExportsData ?? []).filter(
    (job: ReportExport) => job.status === 'pending' || job.status === 'processing'
  )

  // Poll active exports only while at least one is in progress (QA test case 1.04).
  useEffect(() => {
    setShouldPoll(isSuccess && activeExports.length > 0)
  }, [isSuccess, activeExports.length])

  const [markNotificationAsRead, { isLoading: isMarking }] = useMarkNotificationAsReadMutation()
  const [markAllNotificationsAsRead, { isLoading: isMarkingAll }] = useMarkAllNotificationsAsReadMutation()

  if (error) {
    console.error('Notifications API error:', error)
  }

  // Handle different possible response structures
  // Expected: { data: { data: [...], ...pagination }, unread_count: number }
  // Fallback: { data: [...], unread_count: number } (if API doesn't nest pagination)
  const notifications = data?.data?.data ?? (Array.isArray(data?.data) ? data.data : [])
  const unreadCount = data?.unread_count ?? 0

  const handleMarkAsRead = useCallback((notification: AppNotification, e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    
    // Mark as read if unread
    if (notification.read_at === null) {
      markNotificationAsRead({ id: notification.id })
    }
  }, [markNotificationAsRead])

  const handleDownload = useCallback((notification: AppNotification, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    // Mark as read if unread
    if (notification.read_at === null) {
      markNotificationAsRead({ id: notification.id })
    }

    // Open download URL
    if (notification.data.download_url) {
      window.open(notification.data.download_url, '_blank', 'noopener,noreferrer')
    }
  }, [markNotificationAsRead])

  const handleMarkAllAsRead = useCallback(() => {
    if (unreadCount > 0 && !isMarkingAll) {
      markAllNotificationsAsRead()
    }
  }, [unreadCount, isMarkingAll, markAllNotificationsAsRead])

  const formatNotificationDate = useCallback((dateString: string) => {
    try {
      return formatDate(new Date(dateString), 'PPp')
    } catch {
      return dateString
    }
  }, [])

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unreadCount > 0 ? `You have ${unreadCount} unread notifications` : activeExports.length > 0 ? 'You have active export jobs' : 'No unread notifications'}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          {activeExports.length > 0 && unreadCount === 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
              <Loader2 className="h-full w-full animate-spin text-primary" />
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Activity Center</SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="notifications" className="mt-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="inprogress" className="flex items-center gap-2">
              In Progress
              {activeExports.length > 0 && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              Notifications
              {unreadCount > 0 && (
                <Badge
                  variant="destructive"
                  className="h-5 w-5 flex items-center justify-center p-0 text-xs"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inprogress" className="mt-4">
            <div className="flex flex-col space-y-2">
              {activeExports.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bell className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    No jobs are currently running.
                  </p>
                </div>
              )}
              {activeExports.map((exportJob) => (
                <div
                  key={exportJob.id}
                  className="w-full p-4 rounded-lg border border-border bg-accent/50"
                >
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">
                        {exportJob.display_name}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize mt-1">
                        Status: {exportJob.status}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="font-semibold">Completed</div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  disabled={isMarkingAll}
                  className="text-xs"
                >
                  {isMarkingAll ? 'Marking...' : 'Mark all as read'}
                </Button>
              )}
            </div>

            <div>
              {isLoading && (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-full" />
                      <Skeleton className="h-3 w-2/3" />
                    </div>
                  ))}
                </div>
              )}

              {isError && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Failed to load notifications
                  </p>
                  {error && typeof error === 'object' && error !== null && 'data' in error && (
                    <p className="text-xs text-muted-foreground mb-4">
                      {typeof (error as { data?: unknown }).data === 'string' ? (error as { data: string }).data : 'Unknown error'}
                    </p>
                  )}
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              )}

              {!isLoading && !isError && notifications.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Bell className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
                  <p className="text-sm text-muted-foreground">
                    You're all caught up!
                  </p>
                </div>
              )}

              {!isLoading && !isError && notifications.length > 0 && (
                <ScrollArea className="h-[calc(100vh-200px)]">
                  <div className="space-y-1">
                    {notifications.map((notification) => {
                      const isUnread = notification.read_at === null
                      const hasDownload = !!notification.data.download_url
                      
                      return (
                        <div
                          key={notification.id}
                          onClick={(e) => !hasDownload && handleMarkAsRead(notification, e)}
                          className={cn(
                            'w-full p-4 rounded-lg transition-colors',
                            'border border-transparent',
                            isUnread && 'bg-accent/50 border-border',
                            !isUnread && 'opacity-75',
                            !hasDownload && 'hover:bg-accent cursor-pointer'
                          )}
                        >
                          <div className="space-y-2">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start gap-2">
                                  <h4 className={cn(
                                    'text-sm font-medium flex-1',
                                    isUnread && 'font-semibold'
                                  )}>
                                    {notification.data.title}
                                  </h4>
                                  {isUnread && (
                                    <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                  {notification.data.body || notification.data.message}
                                </p>
                                {notification.data.report_title && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {notification.data.report_title}
                                    {notification.data.revision_version && ` (v${notification.data.revision_version})`}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatNotificationDate(notification.created_at)}
                                </p>
                              </div>
                            </div>
                            {hasDownload && (
                              <div className="flex items-center justify-end gap-2 pt-2 border-t">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => handleDownload(notification, e)}
                                  disabled={isMarking}
                                  className="text-xs"
                                >
                                  <Download className="h-3 w-3 mr-1.5" />
                                  Download
                                </Button>
                                {isUnread && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => handleMarkAsRead(notification, e)}
                                    disabled={isMarking}
                                    className="text-xs"
                                  >
                                    Mark as read
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}

